/**
 * Category B — less than 6 months with the current employer (golden case 4:
 * must route to Category B, not fail). Category B has two limbs: current
 * annualised salary at the date of application, and actual income received
 * in the 12 months prior. The thin fact set cannot evidence the second limb,
 * so the outcome is PENDING_EVIDENCE with the requirement explained — never
 * a refusal-shaped NOT_MET.
 *
 * Where the salary already reaches the threshold, the date Category A
 * becomes available (tenure reaching 6 months) is computed and reported
 * (NR-1's sibling: a date, not a phrase).
 */
import { addMonthsISO } from "../dates.js";
import type { EvaluatePredicate, PredicateOutcome } from "./predicate.js";

export const cat_b_route: EvaluatePredicate = (facts, rule, ctx) => {
  const thresholdResult = ctx.results.get("FIN.THRESHOLD");
  if (!thresholdResult || thresholdResult.status !== "MET") {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.THRESHOLD"],
      notes: ["The income requirement cannot be assessed until the applicable threshold is determined."],
    };
  }
  const threshold = thresholdResult.figures?.["threshold"];
  if (threshold === undefined) throw new Error("FIN.THRESHOLD result carries no threshold figure");

  const tenureMonths = numeric(ctx.paramFigure(rule, "tenure_months_figure"));
  const employment = facts.finances.sponsor_employment;
  const income = employment?.gross_annual ?? null;

  const figures: Record<string, number> = { threshold };
  if (income !== null) figures["income"] = income;

  const outcome: PredicateOutcome = {
    status: "PENDING_EVIDENCE",
    figures,
    because: ["FIN.THRESHOLD"],
    notes: [
      "Category B asks for two things: a current gross annual salary at or above the applicable figure, and actual income received over the 12 months before the application at or above the same figure. Evidence of the 12 months of income is needed to assess the second part.",
    ],
  };

  if (employment?.employer_start && income !== null && income >= threshold) {
    outcome.dates = { category_a_available_from: addMonthsISO(employment.employer_start, tenureMonths) };
    outcome.notes = [
      ...(outcome.notes ?? []),
      "On these figures, Category A (which does not ask for the 12-month history) becomes available once the current employment reaches 6 months — the date is shown.",
    ];
  }

  return outcome;
};

function numeric(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}
